import { api } from '@/lib/api';
import type {
  IApiResponse,
  ICreatePersonAndLinkGitHubRequest,
  ICreatePersonAndLinkSlackRequest,
  IGitHubAdminStatus,
  IGitHubCollaboratorsSyncResult,
  IGitHubMemberSyncResult,
  ILinkGitHubRequest,
  ILinkSlackRequest,
  IMarkGitHubAsBotRequest,
  IMarkSlackAsBotRequest,
  IMergePersonsRequest,
  IMergePersonsResponse,
  IMergePreview,
  IPerson,
  IPersonAccountLink,
  IPersonDetail,
  IPersonListQuery,
  IPersonListResponse,
  IPreviouslyUnlinkedRow,
  IRunAutoMatchResponse,
  IUnlinkResponse,
  IUnmatchedGitHubResponse,
  IUnmatchedSlackResponse,
  IUpdatePersonKindRequest,
} from '@skystone/shared';

function buildListUrl(params?: IPersonListQuery): string {
  const search = new URLSearchParams();
  if (params?.search) search.set('search', params.search);
  if (params?.includeTombstoned) search.set('includeTombstoned', 'true');
  if (params?.includeBots) search.set('includeBots', 'true');
  if (params?.includeInactive) search.set('includeInactive', 'true');
  if (params?.department) search.set('department', params.department);
  if (params?.page) search.set('page', String(params.page));
  if (params?.pageSize) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `/admin/people?${query}` : '/admin/people';
}

export const personApi = {
  list: async (params?: IPersonListQuery): Promise<IPersonListResponse> => {
    const result = await api.get<IApiResponse<IPersonListResponse>>(buildListUrl(params));
    return result.data;
  },

  get: async (id: string): Promise<IPersonDetail> => {
    const result = await api.get<IApiResponse<IPersonDetail>>(`/admin/people/${id}`);
    return result.data;
  },

  runAutoMatch: async (): Promise<IRunAutoMatchResponse> => {
    const result = await api.post<IApiResponse<IRunAutoMatchResponse>>('/admin/people/auto-match');
    return result.data;
  },

  listUnmatchedSlack: async (): Promise<IUnmatchedSlackResponse> => {
    const result = await api.get<IApiResponse<IUnmatchedSlackResponse>>(
      '/admin/people/unmatched-slack',
    );
    return result.data;
  },

  linkSlack: async (input: ILinkSlackRequest): Promise<IPersonAccountLink> => {
    const result = await api.post<IApiResponse<IPersonAccountLink>>(
      '/admin/people/link-slack',
      input,
    );
    return result.data;
  },

  createAndLinkSlack: async (
    input: ICreatePersonAndLinkSlackRequest,
  ): Promise<{ person: IPerson; link: IPersonAccountLink }> => {
    const result = await api.post<IApiResponse<{ person: IPerson; link: IPersonAccountLink }>>(
      '/admin/people/create-and-link-slack',
      input,
    );
    return result.data;
  },

  markSlackAsBot: async (
    input: IMarkSlackAsBotRequest,
  ): Promise<{ person: IPerson; link: IPersonAccountLink }> => {
    const result = await api.post<IApiResponse<{ person: IPerson; link: IPersonAccountLink }>>(
      '/admin/people/mark-slack-as-bot',
      input,
    );
    return result.data;
  },

  updateKind: async (id: string, input: IUpdatePersonKindRequest): Promise<IPerson> => {
    const result = await api.patch<IApiResponse<IPerson>>(
      `/admin/people/${id}/kind`,
      input,
    );
    return result.data;
  },

  mergePreview: async (winnerId: string, input: IMergePersonsRequest): Promise<IMergePreview> => {
    const result = await api.post<IApiResponse<IMergePreview>>(
      `/admin/people/${winnerId}/merge-preview`,
      input,
    );
    return result.data;
  },

  merge: async (winnerId: string, input: IMergePersonsRequest): Promise<IMergePersonsResponse> => {
    const result = await api.post<IApiResponse<IMergePersonsResponse>>(
      `/admin/people/${winnerId}/merge`,
      input,
    );
    return result.data;
  },

  unlink: async (linkId: string): Promise<IUnlinkResponse> => {
    const result = await api.post<IApiResponse<IUnlinkResponse>>(
      `/admin/people/links/${linkId}/unlink`,
    );
    return result.data;
  },

  listPreviouslyUnlinked: async (): Promise<IPreviouslyUnlinkedRow[]> => {
    const result = await api.get<IApiResponse<IPreviouslyUnlinkedRow[]>>(
      '/admin/people/previously-unlinked',
    );
    return result.data;
  },

  listUnmatchedGitHub: async (): Promise<IUnmatchedGitHubResponse> => {
    const result = await api.get<IApiResponse<IUnmatchedGitHubResponse>>(
      '/admin/people/unmatched-github',
    );
    return result.data;
  },

  linkGitHub: async (input: ILinkGitHubRequest): Promise<IPersonAccountLink> => {
    const result = await api.post<IApiResponse<IPersonAccountLink>>(
      '/admin/people/link-github',
      input,
    );
    return result.data;
  },

  createAndLinkGitHub: async (
    input: ICreatePersonAndLinkGitHubRequest,
  ): Promise<{ person: IPerson; link: IPersonAccountLink }> => {
    const result = await api.post<IApiResponse<{ person: IPerson; link: IPersonAccountLink }>>(
      '/admin/people/create-and-link-github',
      input,
    );
    return result.data;
  },

  markGitHubAsBot: async (
    input: IMarkGitHubAsBotRequest,
  ): Promise<{ person: IPerson; link: IPersonAccountLink }> => {
    const result = await api.post<IApiResponse<{ person: IPerson; link: IPersonAccountLink }>>(
      '/admin/people/mark-github-as-bot',
      input,
    );
    return result.data;
  },

  github: {
    getStatus: async (): Promise<IGitHubAdminStatus> => {
      const result = await api.get<IApiResponse<IGitHubAdminStatus>>(
        '/admin/people/github/status',
      );
      return result.data;
    },
    triggerMemberSync: async (): Promise<IGitHubMemberSyncResult> => {
      const result = await api.post<IApiResponse<IGitHubMemberSyncResult>>(
        '/admin/people/github/trigger-member-sync',
      );
      return result.data;
    },
    triggerCollaboratorSync: async (): Promise<IGitHubCollaboratorsSyncResult> => {
      const result = await api.post<IApiResponse<IGitHubCollaboratorsSyncResult>>(
        '/admin/people/github/trigger-collaborator-sync',
      );
      return result.data;
    },
  },
};
